import React, { cloneElement, useRef } from 'react';
import Box from '../box';
import { vui } from '../core';
import Portal from '../portal';
import { callAll, cs, mergeRefs } from '../utils';
import { ModalProvider } from './context';
import FocusLock from './focusLock';
import ModalBackdrop from './modalBackdrop';
import ModalContent from './modalContent';
import { manager, useModalManager } from './modalManager';
/**
 * Displays provided content on top of a dark backdrop element with fixed position.
 * By default uses Portal for content and closes when clicking backdrop or pressing 'Escape'.
 * Uses focus and scroll lock to achieve proper accessibility.
 * More about accessibility: https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/dialog_role
 */
export const Modal = vui((props, ref) => {
    const { allowPinchZoom, children, className, contentProps, disableAutoFocus, disableBackdropClose, disableEscClose, disablePortal, disableReturnFocus, disableScrollLock, disableTrapFocus, finalFocusRef, hideBackdrop, initialFocusRef, isOpen, onBackdropClick: onBackdropClickProp, onClose, onEsc, preserveScrollBar, size, variant, ...rest } = props;
    const modalRef = useRef(null);
    const mouseDownTarget = useRef(null);
    useModalManager(modalRef, isOpen, disableScrollLock);
    if (!isOpen)
        return null;
    const onBackdropClick = (e) => {
        e.stopPropagation();
        // Avoid closing modal when click starts inside the content and is released outside
        if (mouseDownTarget.current !== e.target)
            return;
        if (!manager.isTopModal(modalRef))
            return;
        !disableBackdropClose && onClose?.();
        onBackdropClickProp?.(e);
    };
    const onKeyDown = (e) => {
        if (e.key === 'Escape') {
            e.stopPropagation();
            !disableEscClose && onClose?.();
            onEsc?.();
        }
    };
    const onMouseDown = (e) => (mouseDownTarget.current = e.target);
    const childProps = {
        onClick: (e) => {
            e.stopPropagation();
            children?.props?.onClick?.(e);
        },
        tabIndex: children?.props?.tabIndex ?? -1
    };
    const context = { isOpen, onBackdropClick, onClose, size, variant };
    return (React.createElement(ModalProvider, { value: context },
        React.createElement(Portal, { disablePortal: disablePortal },
            React.createElement(Box, { className: cs('vui-modal', className), ref: mergeRefs(ref, modalRef), ...rest, onKeyDown: callAll(props.onKeyDown, onKeyDown), onMouseDown: callAll(props.onMouseDown, onMouseDown) },
                !hideBackdrop && React.createElement(ModalBackdrop, null),
                React.createElement(FocusLock, { autoFocus: !disableAutoFocus, finalFocusRef: finalFocusRef, initialFocusRef: initialFocusRef, isDisabled: disableTrapFocus, returnFocus: !disableReturnFocus },
                    React.createElement(ModalContent, { animation: "fadeDown", ...contentProps }, cloneElement(children, childProps)))))));
});
Modal.Backdrop = ModalBackdrop;
Modal.Content = ModalContent;
export default Modal;
